// Transfer status of the gift (G-Engine: ACCEPTED → PREPARED → DELIVERING → SHIPPED → DELIVERED,
// CANCELED/REFUNDED; legacy Engine: PROCESSING/DONE/ERROR) → timeline stage and state.
// The same rules live in src/lib/transferStatus.ts of the v2–v4 fronts.

const SUCCESS = ['SHIPPED', 'DELIVERED', 'DONE'];
const FAILED = ['CANCELED', 'REFUNDED', 'ERROR'];
// A failure after the order was accepted is marked on stage 2
const STAGE_BY_STATUS = {
  ACCEPTED: 2,
  PROCESSING: 2,
  CANCELED: 2,
  REFUNDED: 2,
  ERROR: 2,
  PREPARED: 3,
  DELIVERING: 3,
  SHIPPED: 4,
  DELIVERED: 4,
  DONE: 4,
};
// No prototype: a status named "constructor" must not look already warned about
const warned = Object.create(null);

export function isTransferSuccess(status) {
  return Boolean(status) && SUCCESS.includes(status);
}

// Terminal state: nothing to poll for. Legacy ERROR may still become DONE by a manager.
export function isTransferTerminal(status, error) {
  return (
    isTransferSuccess(status) ||
    status === 'CANCELED' ||
    status === 'REFUNDED' ||
    Boolean(error && error.type === 1)
  );
}

export function resolveTransfer(status, error) {
  if (!status) return { stage: 1, state: error ? 'error' : 'pending' };
  // Own keys only: a status like "constructor" must not hit Object.prototype
  let stage = Object.prototype.hasOwnProperty.call(STAGE_BY_STATUS, status)
    ? STAGE_BY_STATUS[status]
    : undefined;
  if (!stage) {
    if (!warned[status]) {
      warned[status] = true;
      // eslint-disable-next-line no-console
      console.warn(`Unknown transferStatus "${status}", shown as ACCEPTED`);
    }
    stage = 2;
  }
  if (isTransferSuccess(status)) return { stage: 4, state: 'success' };
  if (FAILED.includes(status) || error) return { stage, state: 'error' };
  return { stage, state: 'pending' };
}

// After this long without a terminal state the "taking longer" hint shows and polling slows down
export const TRANSFER_SLOW_AFTER_MS = 10 * 60 * 1000;

export function isTransferSlow(anchor, now = Date.now()) {
  return now - anchor >= TRANSFER_SLOW_AFTER_MS;
}

export function transferStartKey(giftcode) {
  return `transferStartedAt:${giftcode}`;
}

// localStorage throws in Safari private mode — every failure is swallowed
export function readStoredTransferStart(giftcode) {
  try {
    const raw = localStorage.getItem(transferStartKey(giftcode));
    const value = raw === null ? NaN : Number(raw);
    return Number.isFinite(value) && value > 0 ? value : null;
  } catch (e) {
    return null;
  }
}

// Remembered on a successful user_agreed. A repeated successful agree is only possible after
// a manager resets the link, i.e. it is a new transfer, so the old value is overwritten
export function rememberTransferStart(giftcode, now = Date.now()) {
  try {
    localStorage.setItem(transferStartKey(giftcode), String(now));
  } catch (e) {
    // no stopwatch in private mode
  }
}

// Transfer start: backend field, else localStorage, else unknown
export function resolveTransferStart(fromApi, giftcode) {
  if (fromApi && fromApi > 0) return fromApi;
  return giftcode ? readStoredTransferStart(giftcode) : null;
}

// mm:ss, h:mm:ss from an hour; the client clock may lag the server — never below 00:00
export function formatElapsed(ms) {
  const total = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(total / 3600);
  const mm = String(Math.floor((total % 3600) / 60)).padStart(2, '0');
  const ss = String(total % 60).padStart(2, '0');
  return h > 0 ? `${h}:${mm}:${ss}` : `${mm}:${ss}`;
}
